import { useState, useEffect } from "react"
import { message, Spin, Card, Form } from "antd"
import type { UploadChangeParam } from "antd/es/upload"
import { useUserStore } from "@/stores/user/userStore"
import { usaSapAssetStore } from "@/stores/sap-asset/sapAssetStore"
import { useNavigate } from "react-router"
import { TempLayout } from "@/layouts"
import SapAssetForm from "./components/SapAssetForm"
import { ISapAssetDetail, ISapAssetDto, ISapImportValidationError } from "@/types"
import { confirmSapImportSubmit } from "../shared/utils/confirmSapImportSubmit"
import { EMPTY_SAP_IMPORT_VALIDATION, extractSapImportValidationFromError, normalizeSapImportValidation } from "../shared/utils/sapImportValidation"

const SapAssetsAdd = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const [form] = Form.useForm<ISapAssetDto>()
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewItems, setPreviewItems] = useState<ISapAssetDetail[]>([])
  const [previewIsValid, setPreviewIsValid] = useState(true)
  const [previewTemplateKey, setPreviewTemplateKey] = useState<string | null>(null)
  const [previewSheetName, setPreviewSheetName] = useState<string | null>(null)
  const [previewRequiredFields, setPreviewRequiredFields] = useState<string[]>([])
  const [previewValidationErrors, setPreviewValidationErrors] = useState<ISapImportValidationError[]>([])
  const [canSubmit, setCanSubmit] = useState(false)
  const { createSapAsset, previewSapAsset } = usaSapAssetStore()

  const handleFileChange = async (info: UploadChangeParam) => {
    if (info.file.status === "removed") {
      setFile(null)
      setPreviewItems([])
      setPreviewError(null)
      setPreviewIsValid(EMPTY_SAP_IMPORT_VALIDATION.isValid)
      setPreviewTemplateKey(null)
      setPreviewSheetName(null)
      setPreviewRequiredFields(EMPTY_SAP_IMPORT_VALIDATION.requiredFields)
      setPreviewValidationErrors(EMPTY_SAP_IMPORT_VALIDATION.validationErrors)
      setPreviewModalOpen(false)
      form.setFieldValue("file", undefined)
      return
    }

    let fileObj: File | undefined

    if (info.file instanceof File) {
      fileObj = info.file
    } else if (info.file.originFileObj) {
      fileObj = info.file.originFileObj
    } else if (info.fileList && info.fileList[0]) {
      if (info.fileList[0] instanceof File) {
        fileObj = info.fileList[0]
      } else if (info.fileList[0].originFileObj) {
        fileObj = info.fileList[0].originFileObj
      }
    }

    if (fileObj instanceof File) {
      setFile(fileObj)
      setPreviewError(null)
      setPreviewLoading(true)
      form.setFieldValue("file", fileObj)

      try {
        const res = await previewSapAsset(fileObj)
        const validationState = normalizeSapImportValidation(res.data)
        const items: ISapAssetDetail[] = res.data.items || []
        setPreviewItems(items)
        setPreviewIsValid(validationState.isValid)
        setPreviewTemplateKey(res.data.template_key || null)
        setPreviewSheetName(res.data.sheet_name || null)
        setPreviewRequiredFields(validationState.requiredFields)
        setPreviewValidationErrors(validationState.validationErrors)
        setPreviewModalOpen(true)
      } catch (err: unknown) {
        const errorMessage = err instanceof Error && "response" in err ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || "Gagal preview file" : "Gagal preview file"
        setPreviewError(errorMessage)
        setPreviewItems([])
        setPreviewIsValid(EMPTY_SAP_IMPORT_VALIDATION.isValid)
        setPreviewTemplateKey(null)
        setPreviewSheetName(null)
        setPreviewRequiredFields(EMPTY_SAP_IMPORT_VALIDATION.requiredFields)
        setPreviewValidationErrors(EMPTY_SAP_IMPORT_VALIDATION.validationErrors)
        setPreviewModalOpen(true)
      } finally {
        setPreviewLoading(false)
      }
    }
  }

  useEffect(() => {
    if (file && previewItems.length > 0 && !previewError && previewIsValid) {
      setCanSubmit(true)
    } else {
      setCanSubmit(false)
    }
  }, [file, previewItems, previewError, previewIsValid])

  const handleSubmit = async (values: ISapAssetDto) => {
    if (!file || !canSubmit) {
      if (!previewIsValid) {
        setPreviewModalOpen(true)
        void message.error("Please complete the mandatory fields listed in the preview before submitting.")
        return
      }

      void message.error("File must be previewed and valid before submit!")
      return
    }

    const isConfirmed = await confirmSapImportSubmit("Asset")
    if (!isConfirmed) {
      return
    }

    setLoading(true)
    try {
      await createSapAsset({
        employee_id: values.employee_id,
        fullname: values.fullname,
        level: values.level,
        department: values.department,
        branch: values.branch,
        type_of_request: values.type_of_request,
        note: values.note || "",
        template_key: previewTemplateKey || undefined,
        sheet_name: previewSheetName || undefined,
        assets: previewItems,
      })
      void message.success("Asset created successfully!")
      form.resetFields()
      setFile(null)
      setPreviewItems([])
      setPreviewTemplateKey(null)
      setPreviewSheetName(null)
      setPreviewModalOpen(false)
      navigate("/sapasst/history")
    } catch (err: unknown) {
      const validationState = extractSapImportValidationFromError(err)
      if (validationState.validationErrors.length) {
        setPreviewIsValid(validationState.isValid)
        setPreviewRequiredFields(validationState.requiredFields)
        setPreviewValidationErrors(validationState.validationErrors)
        setPreviewModalOpen(true)
        void message.error("Please complete the mandatory fields listed in the preview before submitting.")
      } else {
        void message.error(err instanceof Error ? err.message : "Failed to create asset")
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    form.setFieldsValue({
      employee_id: userData.employee_id || "",
      fullname: userData.fullname || "",
      level: userData.job_level || "",
      department: userData.organization_name || "",
    })
  }, [userData, form])

  return (
    <TempLayout title="SAP - Create new Asset" navNavigateUrl="/sapasst/history">
      <Card>
        <Spin spinning={loading}>
          <SapAssetForm
            isEditMode={false}
            form={form}
            file={file}
            loading={loading}
            canSubmit={canSubmit}
            onFileChange={info => {
              void handleFileChange(info)
            }}
            onFinish={values => {
              void handleSubmit(values)
            }}
            previewModalOpen={previewModalOpen}
            previewLoading={previewLoading}
            previewError={previewError}
            previewItems={previewItems}
            previewIsValid={previewIsValid}
            previewTemplateKey={previewTemplateKey}
            previewRequiredFields={previewRequiredFields}
            previewValidationErrors={previewValidationErrors}
            setPreviewModalOpen={setPreviewModalOpen}
          />
        </Spin>
      </Card>
    </TempLayout>
  )
}

export default SapAssetsAdd
